"use client";

import { useState } from 'react'
import Image from 'next/image'
import { Star } from "lucide-react";

// Testimonial data
const testimonials = [
  {
    name: "Ethan Trang",
    role: "AI Engineer & Founder",
    company: "Nemo AI",
    image: null, // Using initials fallback
    content:
      "The AI Builder Club was a game-changer for my startup. The insights saved us thousands during our early development phase, and the community has been incredibly supportive.",
    stars: 5,
  },
  {
    name: "Sarah Johnson",
    role: "Founder",
    company: "AImpact",
    image: null, // Using initials fallback
    content:
      "As a solo founder, the resources in the AI Builder Pack have been invaluable. The Replicate credits alone paid for my membership many times over.",
    stars: 5,
  },
  {
    name: "Michael Torres",
    role: "CTO",
    company: "NeuralWorks",
    image: null, // Using initials fallback
    content:
      "The access to premium AI tools through the Builder club helped us prototype in days instead of weeks. Highly recommend for any tech team working with AI.",
    stars: 5,
  },
];

// Helper to generate background colors based on name
const getAvatarColor = (name: string) => {
  const colors = [
    "bg-blue-600",
    "bg-green-600",
    "bg-red-600",
    "bg-blue-600",
    "bg-green-600",
    "bg-red-600",
  ];

  // Simple hash function for the name to get consistent colors
  const hash = name
    .split("")
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length];
};

// Helper to render star rating
const renderStars = (count: number) => {
  return Array(5)
    .fill(0)
    .map((_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${
          i < count ? "text-yellow-500 fill-yellow-500" : "text-gray-400"
        }`}
      />
    ));
};

interface TestimonialsProps {
  isMobile: boolean
}

export default function Testimonials({ isMobile }: TestimonialsProps) {
  const [activeIndex, setActiveIndex] = useState(0)

  const nextTestimonial = () => {
    setActiveIndex((prev) => (prev + 1) % testimonials.length)
  }

  const prevTestimonial = () => {
    setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length)
  }

  return (
    <section
      id="testimonials"
      className="bg-black text-white pt-16"
    >
      <div className="max-w-7xl mx-auto px-4 py-6">
        <h2 className={`${isMobile ? 'text-5xl' : 'text-6xl'} font-bold mb-6`}>
          What Builders Say
        </h2>
        <p className={`${isMobile ? 'text-xl' : 'text-2xl'} text-gray-300 mb-8`}>
          Hear from our community members who've leveraged the AI Builder Pack
          to accelerate their projects
        </p>

        <div className="relative">
          <div className={`grid ${isMobile ? 'grid-cols-1 gap-6' : 'grid-cols-3 gap-8'}`}>
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className={`bg-[#111111] border border-gray-800 rounded-xl p-6 ${
                  isMobile && index !== activeIndex ? 'hidden' : ''
                }`}
              >
                <div className="flex items-center mb-2">
                  <div className="relative w-14 h-14 rounded-full overflow-hidden">
                    {testimonial.image ? (
                      <Image
                        src={testimonial.image}
                        alt={testimonial.name}
                        fill
                        className="object-cover"
                      />
                    ) : (
                      <div
                        className={`w-full h-full ${getAvatarColor(
                          testimonial.name
                        )} flex items-center justify-center`}
                      >
                        <span className="text-white text-lg font-bold">
                          {testimonial.name
                            .split(" ")
                            .map((n) => n[0])
                            .join("")}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="ml-4">
                    <h3 className="text-xl font-semibold">
                      {testimonial.name}
                    </h3>
                    <p className="text-sm text-white/60 mb-2">
                      {testimonial.role}, {testimonial.company}
                    </p>
                    <div className="flex">
                      {renderStars(testimonial.stars)}
                    </div>
                  </div>
                </div>
                
                <p className="text-base text-white/80 leading-relaxed mt-4">
                  "{testimonial.content}"
                </p>
              </div>
            ))}
          </div>

          {isMobile && (
            <div className="flex justify-center mt-6 gap-2">
              <button
                onClick={prevTestimonial}
                className="p-2 bg-zinc-800 rounded-full hover:bg-zinc-700 transition-colors"
                aria-label="Previous testimonial"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m15 18-6-6 6-6"/>
                </svg>
              </button>
              <button
                onClick={nextTestimonial}
                className="p-2 bg-zinc-800 rounded-full hover:bg-zinc-700 transition-colors"
                aria-label="Next testimonial"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m9 18 6-6-6-6"/>
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
